/**
 * KRA Photo Journal – DriveGallery
 *
 * Full gallery overlay that lists ALL photos from the Google Drive folder.
 * Opened from PhotoModal when the user clicks "Xem tất cả ảnh".
 *
 * Features:
 *   - Grid of Drive CDN thumbnails (no R2 needed)
 *   - Lightbox view with prev/next navigation
 *   - Per-photo download button (Worker proxy, ~2000px quality)
 *   - Keyboard: ArrowLeft/Right to navigate, Escape to close
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchGallery,
  sendToCaption,
  sendToMap,
  type GalleryFile,
  type GalleryTags,
  type MapAreaId,
} from "../lib/api.js";
import { DownloadModal } from "./DownloadModal.js";
import { PinPicker } from "./PinPicker.js";
import { ShareLinkModal } from "./ShareLinkModal.js";
import { cn } from "../lib/utils.js";
import { useKeyboard } from "../hooks/useKeyboard.js";
import { shareImages, type ShareItem } from "../lib/share-files.js";

// 20 = roughly matches the iOS share-sheet practical ceiling for image
// files (iOS Photos handles ~25 cleanly; share-sheet starts truncating
// above ~30). Raised from 8 — customers asked to send full sets of
// favourites (typically 12–18 photos per booking) in one tap.
const MAX_SELECTION = 20;

const IS_IOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
// Safari on iOS does NOT contain CriOS/FxiOS/OPiOS
const IS_IOS_SAFARI = IS_IOS && !/CriOS|FxiOS|OPiOS/.test(navigator.userAgent);

/** Save/share an image via iOS Safari Web Share API or desktop blob download.
 *
 *  iOS Safari  → Web Share API → share sheet → "Save Image" → Photos ✅
 *  Desktop     → fetch blob → <a download> ✅
 *
 *  NOTE: iOS Chrome is handled SYNCHRONOUSLY in handleSave (before any await)
 *  by calling window.open() directly — popup blocker blocks it after await.
 */
/**
 * Pick the right file extension for a blob and merge it into the requested
 * filename. If the filename ALREADY ends with an image extension, it's
 * replaced — never stacked. Prevents the `.webp.jpg` double-extension bug
 * that surfaced when CF Image Transformations was disabled (server returned
 * `image/jpeg` while the requested filename ended in `.webp`).
 */
function nameWithBlobExtension(fileName: string, blob: Blob): string {
  const t = (blob.type || "").toLowerCase();
  const ext = t.includes("webp") ? "webp"
            : t.includes("png") ? "png"
            : "jpg";
  const base = fileName.replace(/\.(jpe?g|png|webp)$/i, "");
  return `${base}.${ext}`;
}

async function saveFile(downloadUrl: string, fileName: string, directImageUrl: string): Promise<void> {
  // ── iOS Safari: Web Share API (opens native share sheet) ─────────────────
  if (IS_IOS_SAFARI && typeof navigator.share === "function") {
    try {
      const resp = await fetch(downloadUrl);
      const blob = await resp.blob();
      const name = nameWithBlobExtension(fileName, blob);
      const file = new File([blob], name, { type: blob.type });

      if (typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: name });
        return;
      }
    } catch {
      // canShare/share failed — fall through to desktop download
    }
  }

  // ── Desktop: fetch blob → <a download> ───────────────────────────────────
  const resp = await fetch(downloadUrl);
  const blob = await resp.blob();
  const name = nameWithBlobExtension(fileName, blob);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

interface DriveGalleryProps {
  recordId: string;
  chatName: string;
  driveLink?: string;
  isReadOnly?: boolean;
  onClose: () => void;
}

/** Build a Drive CDN thumbnail URL for display (not download) */
function driveThumbnailUrl(fileId: string, size: "thumb" | "full"): string {
  const sz = size === "thumb" ? "w400" : "w1600";
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=${sz}`;
}

/** Worker proxy URL for downloading (~2000px), EXIF stripped */
function driveDownloadUrl(fileId: string): string {
  const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api";
  return `${API_BASE}/drive/${fileId}/download`;
}

/**
 * Worker proxy URL for the WordPress-optimised download — same 2048px master
 * but re-encoded as WebP @ q82 (~3-4× smaller than the JPEG default). One
 * file per photo; the WordPress media library / theme generates the srcset.
 *
 * When `slug` is provided it overrides the default filename — the worker
 * sanitises it server-side and falls back to the auto pattern if invalid.
 */
function driveDownloadWebpUrl(fileId: string, slug?: string | null): string {
  const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api";
  const params = new URLSearchParams({ format: "webp" });
  if (slug) params.set("filename", slug);
  return `${API_BASE}/drive/${fileId}/download?${params.toString()}`;
}

/** Replace a filename's extension. `photo.jpg` + `webp` → `photo_web.webp` */
function withExtension(name: string, ext: string): string {
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base}_web.${ext}`;
}

/**
 * Worker proxy URL for inline image display, EXIF stripped.
 * Used as <img src> so iOS "Save to Photos" on long-press will save
 * with today's date (not the original capture date).
 */
function driveInlineUrl(fileId: string): string {
  const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api";
  return `${API_BASE}/drive/${fileId}/download?inline=1`;
}

export function DriveGallery({ recordId, chatName, driveLink, isReadOnly, onClose }: DriveGalleryProps) {
  const [files, setFiles] = useState<GalleryFile[] | null>(null);
  // Booking-level tags — kimono / shoot / season / scene / kimono_color.
  // Shared across every photo in the folder, used to build SEO filenames.
  const [galleryTags, setGalleryTags] = useState<GalleryTags | null>(null);
  // PC-only quick-download modal triggered from a grid thumbnail icon.
  // Holds the file the user wants to download with a custom filename.
  const [downloadModalFile, setDownloadModalFile] = useState<GalleryFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  // ── Selection mode state ─────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [showPinPicker, setShowPinPicker] = useState(false);
  const [usingBackup, setUsingBackup] = useState(false);
  const [toast, setToast] = useState<{ message: string; captionUrl?: string } | null>(null);

  const toggleSelect = useCallback((fileId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else if (next.size < MAX_SELECTION) {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Bulk save to device — Web Share API on mobile (→ iOS Photos / WhatsApp),
  // sequential blob download fallback elsewhere. Operates on the same
  // `selectedIds` as Caption / Map, so the user picks once.
  const handleSaveToDevice = useCallback(async () => {
    if (selectedIds.size === 0 || sending) return;
    if (!files) return;
    const picked = files.filter((f) => selectedIds.has(f.fileId));
    if (picked.length === 0) return;

    const items: ShareItem[] = picked.map((f) => ({
      url: driveDownloadWebpUrl(f.fileId),
      // Strip the original extension — shareImages re-applies the right one
      // based on the blob MIME type returned by the worker.
      baseName: f.name.replace(/\.[^.]+$/, ""),
    }));

    setSending(true);
    try {
      const result = await shareImages(items, { title: chatName });
      if (result.kind === "shared") {
        setToast({ message: `Đã chia sẻ ${result.count} ảnh` });
        clearSelection();
      } else if (result.kind === "downloaded") {
        setToast({ message: `Đã tải ${result.count} ảnh` });
        clearSelection();
      } else if (result.kind === "error") {
        setToast({ message: `Lỗi lưu: ${result.message}` });
      }
      // cancelled → keep selection, no toast
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSending(false);
    }
  }, [selectedIds, sending, files, chatName, clearSelection]);

  const handleSendToCaption = useCallback(async () => {
    if (selectedIds.size === 0 || sending) return;
    setSending(true);
    try {
      const result = await sendToCaption(recordId, [...selectedIds]);
      setToast({ message: `Đã tạo draft — ${selectedIds.size} ảnh`, captionUrl: result.captionUrl });
      clearSelection();
      // Auto-dismiss toast after 6 seconds
      setTimeout(() => setToast(null), 6000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Lỗi không xác định";
      setToast({ message: `Lỗi: ${msg}` });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSending(false);
    }
  }, [selectedIds, sending, recordId, clearSelection]);

  const handleSendToMap = useCallback(
    (pinNumber: number, area: MapAreaId) => {
      if (selectedIds.size === 0) return;
      const fileIdsToSend = [...selectedIds];
      const count = fileIdsToSend.length;

      // Close picker + clear selection immediately — upload runs in background
      setShowPinPicker(false);
      clearSelection();
      setToast({
        message: `Đang gửi ${count} ảnh tới Pin ${pinNumber}…`,
      });

      sendToMap(recordId, fileIdsToSend, pinNumber, area)
        .then((result) => {
          setToast({
            message: `Đã gửi ${result.uploadedCount} ảnh tới Pin ${pinNumber}`,
          });
          setTimeout(() => setToast(null), 6000);
        })
        .catch((err) => {
          const msg = err instanceof Error ? err.message : "Lỗi không xác định";
          setToast({ message: `Lỗi gửi Map: ${msg}` });
          setTimeout(() => setToast(null), 4000);
        });
    },
    [selectedIds, recordId, clearSelection],
  );

  // Load gallery on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setFiles(null);

    fetchGallery(recordId)
      .then((data) => {
        if (!cancelled) {
          setFiles(data.files);
          setGalleryTags(data.tags ?? null);
          setUsingBackup(Boolean(data.usingBackup));
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Không thể tải danh sách ảnh");
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [recordId]);

  const count = files?.length ?? 0;

  const lightboxPrev = useCallback(() => {
    if (lightboxIndex === null || lightboxIndex <= 0) return;
    setLightboxIndex(lightboxIndex - 1);
  }, [lightboxIndex]);

  const lightboxNext = useCallback(() => {
    if (lightboxIndex === null || !files || lightboxIndex >= files.length - 1) return;
    setLightboxIndex(lightboxIndex + 1);
  }, [lightboxIndex, files]);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  useKeyboard({
    ArrowLeft: lightboxIndex !== null ? lightboxPrev : () => {},
    ArrowRight: lightboxIndex !== null ? lightboxNext : () => {},
    Escape: lightboxIndex !== null ? closeLightbox : onClose,
  });

  return (
    <>
      {/* Backdrop */}
      <motion.div
        key="gallery-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[70] bg-black/92"
        onClick={onClose}
      />

      {/* Gallery panel */}
      <motion.div
        key="gallery-panel"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={cn(
          "fixed z-[71] flex flex-col",
          "inset-0 md:inset-4 md:rounded-2xl md:overflow-hidden",
          "bg-[#0e0d0b]"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div>
            <h2 className="text-white text-sm font-semibold leading-tight">{chatName}</h2>
            <p className="text-white/40 text-xs mt-0.5">
              {loading ? "Đang tải…" : error ? "Lỗi tải ảnh" : `${count} ảnh`}
              {usingBackup && !loading && !error && (
                <span className="ml-2 text-amber-400/90">· bản Drive của bạn (link gốc đã mất)</span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Share for customer (staff only) */}
            {!isReadOnly && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowShareModal(true); }}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-lg",
                  "bg-[#B76E6E]/20 hover:bg-[#B76E6E]/30 transition-colors",
                  "text-[#E8B5B5] hover:text-white text-xs"
                )}
                title="Tạo link công khai cho khách"
              >
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/>
                  <circle cx="6" cy="12" r="3"/>
                  <circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                <span className="hidden sm:inline">Share khách</span>
              </button>
            )}

            {/* Open Drive folder link */}
            {driveLink && (
              <a
                href={driveLink}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-lg",
                  "bg-white/8 hover:bg-white/14 transition-colors",
                  "text-white/60 hover:text-white text-xs"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <DriveIcon />
                <span className="hidden sm:inline">Mở Drive</span>
              </a>
            )}

            {/* Close button */}
            <button
              onClick={onClose}
              className={cn(
                "w-9 h-9 flex items-center justify-center rounded-full",
                "bg-white/8 hover:bg-white/16 transition-colors text-white"
              )}
              aria-label="Đóng gallery"
            >
              <svg width={13} height={13} viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M1 1l11 11M12 1L1 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <div className="w-6 h-6 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
              <p className="text-white/40 text-xs">Đang quét thư mục Drive…</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-48 gap-2 text-center px-4">
              <p className="text-white/50 text-sm">{error}</p>
              <p className="text-white/30 text-xs">Thử mở thư mục Drive trực tiếp bên trên</p>
            </div>
          )}

          {files && files.length === 0 && (
            <div className="flex items-center justify-center h-48">
              <p className="text-white/40 text-sm">Không tìm thấy ảnh nào</p>
            </div>
          )}

          {files && files.length > 0 && (
            <div className={cn(
              "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2",
              selectedIds.size > 0 && "pb-20" // Space for floating bar
            )}>
              {files.map((file, idx) => (
                <GalleryThumb
                  key={file.fileId}
                  file={file}
                  index={idx}
                  onOpenLightbox={setLightboxIndex}
                  downloadUrl={driveDownloadUrl(file.fileId)}
                  isSelected={selectedIds.has(file.fileId)}
                  onToggleSelect={toggleSelect}
                  selectionCount={selectedIds.size}
                  onOpenDownloadModal={setDownloadModalFile}
                />
              ))}
            </div>
          )}
        </div>

        {/* Selection floating bar */}
        <AnimatePresence>
          {selectedIds.size > 0 && (
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className={cn(
                "absolute bottom-0 left-0 right-0 z-10",
                "flex items-center justify-between gap-3 px-5 py-4",
                "bg-[#1a1918]/95 backdrop-blur-md border-t border-white/8"
              )}
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={clearSelection}
                  className="text-white/50 hover:text-white text-xs transition-colors"
                >
                  ✕ Bỏ chọn
                </button>
                <span className="text-white/70 text-xs font-medium">
                  {selectedIds.size}/{MAX_SELECTION} ảnh
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveToDevice}
                  disabled={sending}
                  className={cn(
                    "flex items-center gap-1.5 px-4 py-2.5 rounded-xl",
                    "bg-white text-[#1a1918] hover:bg-white/90 active:bg-white/80",
                    "text-sm font-semibold transition-colors",
                    "shadow-lg shadow-black/30",
                    sending && "opacity-60 cursor-wait"
                  )}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                  <span>Lưu</span>
                </button>

                <button
                  onClick={handleSendToCaption}
                  disabled={sending}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2.5 rounded-xl",
                    "bg-[#B76E6E] hover:bg-[#a55f5f] active:bg-[#945454]",
                    "text-white text-sm font-medium transition-colors",
                    "shadow-lg shadow-[#B76E6E]/20",
                    sending && "opacity-60 cursor-wait"
                  )}
                >
                  {sending ? (
                    <>
                      <SpinnerIcon />
                      <span>Đang gửi…</span>
                    </>
                  ) : (
                    <span>Caption</span>
                  )}
                </button>

                <button
                  onClick={() => setShowPinPicker(true)}
                  disabled={sending}
                  className={cn(
                    "flex items-center gap-1.5 px-4 py-2.5 rounded-xl",
                    "bg-[#5B8A8A] hover:bg-[#4d7676] active:bg-[#3f6363]",
                    "text-white text-sm font-medium transition-colors",
                    "shadow-lg shadow-[#5B8A8A]/20",
                    sending && "opacity-60 cursor-wait"
                  )}
                >
                  <span>Map</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* PinPicker overlay */}
        <AnimatePresence>
          {showPinPicker && (
            <PinPicker
              onSelect={handleSendToMap}
              onClose={() => setShowPinPicker(false)}
              photoCount={selectedIds.size}
            />
          )}
        </AnimatePresence>

        {/* Toast notification */}
        <AnimatePresence>
          {toast && (
            <motion.div
              initial={{ y: -40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -40, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={cn(
                "absolute top-16 left-1/2 -translate-x-1/2 z-20",
                "flex items-center gap-3 px-4 py-3 rounded-xl",
                "bg-white/10 backdrop-blur-lg border border-white/10",
                "text-white text-sm shadow-xl"
              )}
            >
              <span>{toast.message}</span>
              {toast.captionUrl && (
                <a
                  href={toast.captionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#B76E6E] font-medium hover:underline whitespace-nowrap"
                >
                  Mở Caption →
                </a>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && files && files[lightboxIndex] && (
          <Lightbox
            file={files[lightboxIndex]}
            index={lightboxIndex}
            total={files.length}
            onPrev={lightboxPrev}
            onNext={lightboxNext}
            onClose={closeLightbox}
            downloadUrl={driveDownloadUrl(files[lightboxIndex].fileId)}
          />
        )}
      </AnimatePresence>

      {/* SEO download modal — opened from the WEB icon on grid thumbnails. */}
      <AnimatePresence>
        {downloadModalFile && (
          <DownloadModal
            file={downloadModalFile}
            thumbnailUrl={driveThumbnailUrl(downloadModalFile.fileId, "full")}
            tags={galleryTags}
            onClose={() => setDownloadModalFile(null)}
            buildDownloadUrl={(fileId, slug) => driveDownloadWebpUrl(fileId, slug)}
            onDownload={saveFile}
          />
        )}
      </AnimatePresence>

      {/* Share-with-customer modal */}
      {showShareModal && (
        <ShareLinkModal
          recordId={recordId}
          defaultTitle={chatName}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );
}

// ── GalleryThumb ──────────────────────────────────────────────────────────────

function GalleryThumb({
  file,
  index,
  onOpenLightbox,
  downloadUrl,
  isSelected,
  onToggleSelect,
  selectionCount,
  onOpenDownloadModal,
}: {
  file: GalleryFile;
  index: number;
  onOpenLightbox: (idx: number) => void;
  downloadUrl: string;
  isSelected: boolean;
  onToggleSelect: (fileId: string) => void;
  selectionCount: number;
  /** PC-only — opens the SEO download modal for this file. Undefined on
   *  mobile/touch where the modal is hidden. */
  onOpenDownloadModal?: (file: GalleryFile) => void;
}) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saving) return;
    if (IS_IOS && !IS_IOS_SAFARI) {
      window.open(driveThumbnailUrl(file.fileId, "full"), "_blank");
      return;
    }
    setSaving(true);
    try {
      await saveFile(downloadUrl, file.name, driveThumbnailUrl(file.fileId, "full"));
    } catch { /* user cancelled or error */ }
    finally { setSaving(false); }
  }, [saving, downloadUrl, file.name, file.fileId]);

  const handleClick = useCallback(() => {
    // If any photos are already selected, tapping toggles selection
    if (selectionCount > 0) {
      onToggleSelect(file.fileId);
    } else {
      onOpenLightbox(index);
    }
  }, [selectionCount, onToggleSelect, file.fileId, onOpenLightbox, index]);

  const handleSelectButton = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelect(file.fileId);
  }, [onToggleSelect, file.fileId]);

  return (
    <div
      className={cn(
        "relative group rounded-lg overflow-hidden bg-white/5 transition-all duration-150",
        isSelected && "ring-2 ring-[#B76E6E] ring-offset-1 ring-offset-[#0e0d0b]"
      )}
      style={{ aspectRatio: "3/4" }}
    >
      {/* Thumbnail image */}
      <img
        src={driveThumbnailUrl(file.fileId, "thumb")}
        alt={file.name}
        loading="lazy"
        onLoad={() => setImgLoaded(true)}
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300 cursor-pointer",
          imgLoaded ? "opacity-100" : "opacity-0",
          isSelected && "brightness-75"
        )}
        onClick={handleClick}
      />

      {/* Full-res overlay for iOS long-press "Save to Photos".
          Nearly-invisible (not fully 0) so iOS Safari reliably surfaces the
          callout menu. iOS-only — on desktop this layer would steal clicks
          from the download button below.

          NOTE: opacity bumped from 0.001 → 0.01. Newer iOS Safari (17+)
          skips hit-testing on elements with opacity < ~0.01 as a paint
          optimisation, which silently broke the long-press save callout
          for users on the latest iPhones. 0.01 is still invisible to the
          eye but registers in Safari's hit-test pipeline. */}
      {IS_IOS && (
        <img
          src={driveInlineUrl(file.fileId)}
          alt={file.name}
          loading="lazy"
          aria-hidden="true"
          draggable={false}
          className="absolute inset-0 z-10 w-full h-full object-cover cursor-pointer"
          style={{
            opacity: 0.01,
            pointerEvents: "auto",
            WebkitTouchCallout: "default",
          }}
          onClick={handleClick}
        />
      )}

      {/* Skeleton while loading */}
      {!imgLoaded && (
        <div className="absolute inset-0 bg-white/5 animate-pulse" />
      )}

      {/* Selection checkbox — always visible on top-left */}
      <button
        onClick={handleSelectButton}
        className={cn(
          "absolute top-2 left-2 z-20 w-7 h-7 rounded-full",
          "flex items-center justify-center transition-all duration-150",
          "border-2 shadow-md",
          isSelected
            ? "bg-[#B76E6E] border-[#B76E6E] text-white scale-100"
            : "bg-black/40 border-white/40 text-transparent scale-90",
          !isSelected && "group-hover:scale-100 group-hover:border-white/60"
        )}
        aria-label={isSelected ? "Bỏ chọn" : "Chọn ảnh"}
      >
        <svg width={12} height={12} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 6l3 3 5-5" />
        </svg>
      </button>

      {/* Hover overlay with download — mouse devices only.
          On PC (≥1024px + fine pointer) renders TWO icons: the original
          JPEG quick-save and the WebP-with-custom-filename trigger that
          opens DownloadModal. On smaller mouse devices we keep only the
          original button so the overlay isn't crowded. */}
      <div className={cn(
        "absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-150",
        "items-end justify-end gap-1.5 p-2 opacity-0 group-hover:opacity-100",
        "pointer-events-none",
        "hidden [@media(hover:hover)]:flex"
      )}>
        {onOpenDownloadModal && (
          <button
            onClick={(e) => { e.stopPropagation(); onOpenDownloadModal(file); }}
            className={cn(
              "pointer-events-auto",
              "hidden [@media(pointer:fine)_and_(min-width:1024px)]:flex",
              "w-8 h-8 items-center justify-center rounded-full",
              "bg-[#B76E6E] hover:bg-[#a55e5e] text-white transition-colors shadow-lg",
              "ring-1 ring-white/20",
            )}
            aria-label="Tải tối ưu web"
            title="Tải tối ưu web (chỉnh tên trước khi tải)"
          >
            <WebDownloadIcon />
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className={cn(
            "pointer-events-auto",
            "w-8 h-8 flex items-center justify-center rounded-full",
            "bg-black/70 hover:bg-black/90 text-white transition-colors shadow-lg",
            saving && "opacity-50"
          )}
          aria-label="Lưu ảnh"
          title="Lưu ảnh (JPEG gốc)"
        >
          {saving ? <SpinnerIcon /> : <DownloadIcon />}
        </button>
      </div>
    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────

function Lightbox({
  file,
  index,
  total,
  onPrev,
  onNext,
  onClose,
  downloadUrl,
}: {
  file: GalleryFile;
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  downloadUrl: string;
}) {
  const [saving, setSaving] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx > 0) onPrev();
    else onNext();
  }, [onPrev, onNext]);

  const handleSave = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saving) return;
    // iOS Chrome: window.open must be called synchronously inside user gesture
    if (IS_IOS && !IS_IOS_SAFARI) {
      window.open(driveThumbnailUrl(file.fileId, "full"), "_blank");
      return;
    }
    setSaving(true);
    try {
      await saveFile(downloadUrl, file.name, driveThumbnailUrl(file.fileId, "full"));
    } catch { /* user cancelled or error */ }
    finally { setSaving(false); }
  }, [saving, downloadUrl, file.name, file.fileId]);

  return (
    <>
      <motion.div
        key="lightbox-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-[80] bg-black/97"
        onClick={onClose}
      />

      <motion.div
        key="lightbox-content"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed inset-0 z-[81] flex items-center justify-center p-4"
        onClick={onClose}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={driveInlineUrl(file.fileId)}
          alt={file.name}
          className="max-w-full max-h-full object-contain rounded-lg"
          style={{ WebkitTouchCallout: "default", WebkitUserSelect: "none", userSelect: "none" }}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Top bar */}
        <div
          className="absolute top-4 left-0 right-0 flex items-center justify-between px-4"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-white/40 text-xs bg-black/50 px-2.5 py-1 rounded-full">
            {index + 1} / {total}
          </span>

          <div className="flex items-center gap-2">
            {/* The WordPress-optimised WebP flow lives entirely in the
                grid-thumbnail download modal now — the lightbox just
                handles the JPEG quick-save. */}
            <button
              onClick={handleSave}
              disabled={saving}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2 rounded-full",
                "bg-white/15 hover:bg-white/25 transition-colors",
                "text-white text-sm font-medium border border-white/30",
                "shadow-lg shadow-black/20",
                saving && "opacity-60",
              )}
            >
              {saving ? <SpinnerIcon /> : <DownloadIcon />}
              <span>{saving ? "Đang tải…" : "Lưu ảnh"}</span>
            </button>

            {/* Close */}
            <button
              onClick={onClose}
              className={cn(
                "w-9 h-9 flex items-center justify-center rounded-full",
                "bg-black/70 hover:bg-black/90 text-white border border-white/15",
                "transition-colors"
              )}
              aria-label="Đóng"
            >
              <svg width={13} height={13} viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M1 1l11 11M12 1L1 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Nav arrows */}
        {index > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className={cn(
              "absolute left-4 top-1/2 -translate-y-1/2",
              "w-10 h-10 flex items-center justify-center rounded-full",
              "bg-black/60 hover:bg-black/80 text-white border border-white/10 transition-colors"
            )}
            aria-label="Ảnh trước"
          >
            <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
              <path d="M9 2.5L4.5 7 9 11.5"/>
            </svg>
          </button>
        )}

        {index < total - 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className={cn(
              "absolute right-4 top-1/2 -translate-y-1/2",
              "w-10 h-10 flex items-center justify-center rounded-full",
              "bg-black/60 hover:bg-black/80 text-white border border-white/10 transition-colors"
            )}
            aria-label="Ảnh tiếp theo"
          >
            <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
              <path d="M5 2.5L9.5 7 5 11.5"/>
            </svg>
          </button>
        )}

        {/* Bottom hint */}
        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 text-white/40 text-[11px] pointer-events-none">
          <span className="[@media(hover:hover)]:hidden">Vuốt để chuyển ảnh</span>
          <span className="hidden [@media(hover:hover)]:inline">Nhấn để đóng · ← → để điều hướng</span>
        </p>
      </motion.div>
    </>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function DownloadIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v13M7 11l5 5 5-5"/>
      <path d="M5 20h14"/>
    </svg>
  );
}

/** Download icon variant for the WordPress / SEO flow — same arrow with a
 *  small "WEB" tag underneath so the two grid icons read as different
 *  actions at a glance. */
function WebDownloadIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path strokeWidth={2.5} d="M12 3v9M7 8l5 4 5-4"/>
      <path strokeWidth={2.5} d="M5 16h14"/>
      <text x="12" y="22" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="currentColor" stroke="none">
        WEB
      </text>
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="animate-spin">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

function DriveIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="currentColor">
      <path d="M4.5 21L9 13.5H1.5L9 2.25h6L10.5 9H18L13.5 21H4.5z" opacity={0.6}/>
      <path d="M10.5 9H18L22.5 21H13.5L10.5 9z" opacity={0.8}/>
      <path d="M9 2.25h6L19.5 9H13.5L9 2.25z"/>
    </svg>
  );
}
